import { useCallback } from 'react';
import { useApiClient } from '@hooks/useApiClient';

/**
 * Custom hook for NFT-related data operations
 * Handles user NFTs, buy NFTs, and top fundraiser data
 */
export const useNftData = () => {
  const { get } = useApiClient();

  // Get user's NFT data
  const getUserNfts = useCallback(async (walletAddress) => {
    try {
      const response = await get(`/fundraisin/my-nft/${walletAddress || 0}`);
      return {
        data: response.data || [],
        newest: response.newest || false,
      };
    } catch (error) {
      console.error('Failed to load NFT data:', error);
      return { data: [], newest: false };
    }
  }, [get]);

  // Get buy NFT data with pagination
  const getBuyNfts = useCallback(async (walletAddress, page = 1, limit = 10) => {
    try {
      const pagination = page && limit ? `?page=${page}&limit=${limit}` : '';
      const response = await get(`/fundraisin/buy-nft/${walletAddress || 0}${pagination}`);
      return response.data || [];
    } catch (error) {
      console.error('Failed to load buy NFT data:', error);
      return [];
    }
  }, [get]);

  // Get top fundraiser data
  const getTopFundraisers = useCallback(async (walletAddress) => {
    try {
      const response = await get(`/fundraisin/top-nft/${walletAddress || 0}`);
      return response || [];
    } catch (error) {
      console.error('Failed to load top data:', error);
      return [];
    }
  }, [get]);

  // Load all wallet-dependent NFT data
  const loadWalletNftData = useCallback(async (walletAddress) => {
    try {
      const [userNfts, buyNfts, topData] = await Promise.all([
        getUserNfts(walletAddress),
        getBuyNfts(walletAddress),
        getTopFundraisers(walletAddress),
      ]);

      return {
        nftData: userNfts.data,
        buyNftData: buyNfts,
        topNftData: topData,
        newest: userNfts.newest,
      };
    } catch (error) {
      console.error('Failed to load wallet NFT data:', error);
      return {
        nftData: [],
        buyNftData: [],
        topNftData: [],
        newest: false,
      };
    }
  }, [getUserNfts, getBuyNfts, getTopFundraisers]);

  return {
    getUserNfts,
    getBuyNfts,
    getTopFundraisers,
    loadWalletNftData,
  };
};
